import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  Factory,
  LogOut,
  HardDrive,
  UserCog,
  Settings,
  HelpCircle,
  Calendar,
  LayoutGrid,
  Archive,
  History,
  FileText,
  Download,
  Mail,
  ClipboardList,
  PlusCircle,
  ListChecks,
  SquareArrowUp,
} from "lucide-react";
import { ProjectStatusQuickControl } from "./ProjectStatusQuickControl";
import { KanbanAppearanceSettings } from "./KanbanAppearanceSettings";
import { DashboardAppearanceSettings } from "./DashboardAppearanceSettings";
import { GAMER_LOGO_SRC } from "../gamerLogoData";
import { APP_VERSION } from "../version";

const ROLE_BADGES = {
  graphics_admin: {
    label: "Graphics Admin",
    className: "px-2 py-1 rounded text-sm font-medium bg-emerald-950/30 text-emerald-400 border border-emerald-800",
  },
  sales_manager: {
    label: "Sales Ops Management",
    className: "px-2 py-1 rounded text-sm font-medium bg-amber-950/30 text-amber-400 border border-amber-800",
  },
  sales_rep: {
    label: "Sales Ops",
    className: "px-2 py-1 rounded text-sm font-medium bg-blue-950/30 text-blue-400 border border-blue-800",
  },
  sales_viewer: {
    label: "Viewer",
    className: "px-2 py-1 rounded text-sm font-medium bg-gray-800/60 text-gray-300 border border-gray-700",
  },
};

const SidebarSectionLabel = ({ children }) => (
  <>
    <div className="border-t border-border my-3 mx-2" />
    <p className="text-xs text-muted-foreground px-4 mb-2 uppercase tracking-wider">
      {children}
    </p>
  </>
);

const SettingsRoleBadgeSync = () => {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/settings") return undefined;

    const badge = ROLE_BADGES[user?.role] || ROLE_BADGES.sales_viewer;

    const syncBadge = () => {
      const roleLabel = Array.from(document.querySelectorAll("label")).find(
        (element) => (element.textContent || "").trim() === "Role",
      );
      const badgeElement = roleLabel?.nextElementSibling?.querySelector("span");
      if (!badgeElement) return false;

      if (badgeElement.textContent !== badge.label) {
        badgeElement.textContent = badge.label;
      }
      if (badgeElement.className !== badge.className) {
        badgeElement.className = badge.className;
      }
      return true;
    };

    if (syncBadge()) return undefined;

    const observer = new MutationObserver(() => {
      if (syncBadge()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname, user?.role]);

  return null;
};

const Sidebar = () => {
  const {
    user,
    logout,
    isAdmin,
    isManager,
    isSalesRep,
    canCreateProject,
    canAccessIntakeQueue,
    roleLabel,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const projectsLabel = "Projects";

  const workflowItems = [
    ...(canCreateProject
      ? [{ path: "/intake", icon: PlusCircle, label: "New Design Request", shareable: true }]
      : []),
    ...(canAccessIntakeQueue
      ? [{ path: "/intake-submissions", icon: ClipboardList, label: "Intake Queue" }]
      : []),
    { path: "/projects", icon: FolderKanban, label: projectsLabel },
    { path: "/kanban", icon: LayoutGrid, label: "Graphics Queue" },
    { path: "/calendar", icon: Calendar, label: "Calendar" },
    { path: "/archive", icon: Archive, label: "Completed Archive" },
  ];

  const graphicsAdminItems = [
    { path: "/project-archive", icon: Archive, label: "Archived Projects" },
    { path: "/release-history", icon: History, label: "Release History" },
    { path: "/sharepoint", icon: HardDrive, label: "SharePoint" },
    { path: "/email-inbox", icon: Mail, label: "Email Inbox" },
    { path: "/spiro", icon: Download, label: "Spiro Import" },
    { path: "/audit-trail", icon: FileText, label: "Audit Trail" },
  ];

  const administrationItems = [
    { path: "/customers", icon: Users, label: "Customers" },
    { path: "/manufacturers", icon: Factory, label: "Manufacturers" },
    { path: "/users", icon: UserCog, label: "Users" },
    { path: "/intake-form-options", icon: ListChecks, label: "Dropdown Management" },
  ];

  const preferenceItems = [
    { path: "/help", icon: HelpCircle, label: "Help" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  const shareIntakeForm = async () => {
    const url = `${window.location.origin}/intake`;
    const shareData = {
      title: "Gamer Packaging Customer Design Request",
      text: "Please use this form to submit your Gamer Packaging design request.",
      url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Customer intake link copied");
    } catch {
      window.prompt("Copy the customer intake link:", url);
    }
  };

  const renderLink = (item) => {
    const link = (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase()}`}
        className={`nav-item ${location.pathname === item.path ? "active" : ""}`}
      >
        <item.icon size={20} />
        <span>{item.label}</span>
      </Link>
    );

    if (!item.shareable) return link;

    return (
      <div key={`${item.path}-share-row`} className="flex items-center gap-1">
        <div className="min-w-0 flex-1">{link}</div>
        <button
          type="button"
          onClick={shareIntakeForm}
          className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          title="Share customer intake form"
          aria-label="Share customer intake form"
          data-testid="share-intake-form"
        >
          <SquareArrowUp size={20} />
        </button>
      </div>
    );
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="bg-white rounded-lg p-2 mb-2">
          <img src={GAMER_LOGO_SRC} alt="Gamer Packaging" className="h-8 w-auto" />
        </div>
        <p className="text-xs text-muted-foreground">InkSight v{APP_VERSION}</p>
      </div>

      <nav className="sidebar-nav">
        {renderLink({ path: "/", icon: LayoutDashboard, label: "Dashboard" })}

        <SidebarSectionLabel>Workflow</SidebarSectionLabel>
        {workflowItems.map(renderLink)}

        {isAdmin && (
          <>
            <SidebarSectionLabel>Graphics Admin</SidebarSectionLabel>
            {graphicsAdminItems.map(renderLink)}

            <SidebarSectionLabel>Administration</SidebarSectionLabel>
            {administrationItems.map(renderLink)}
          </>
        )}

        <SidebarSectionLabel>Preferences</SidebarSectionLabel>
        {preferenceItems.map(renderLink)}
      </nav>

      <div className="sidebar-footer">
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-medium">
            {user?.full_name?.charAt(0) || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground">{roleLabel}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="nav-item w-full text-red-400 hover:text-red-300 hover:bg-red-950/30"
        >
          <LogOut size={20} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const match = location.pathname.match(/^\/projects\/([^/]+)$/);
  const projectId = match && match[1] !== "new" ? match[1] : null;
  const showAppearanceSettings = location.pathname === "/settings";

  return (
    <div className="app-layout">
      <Sidebar />
      <SettingsRoleBadgeSync />
      <main className="main-content">
        <div className="p-8">
          {projectId && <ProjectStatusQuickControl projectId={projectId} />}
          {children}
          {showAppearanceSettings && <KanbanAppearanceSettings />}
          {showAppearanceSettings && <DashboardAppearanceSettings />}
        </div>
      </main>
    </div>
  );
};
